import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Dropdown from "../../components/ui/dropdown";
import Input from "../../components/ui/input";
import Button from "../../components/ui/button";
import styles from "./create-record.module.css";
import FormSection from "../../components/screens/form-section";
import Checkbox from "../../components/ui/checkbox";
import ContactItem from "../../components/screens/contact-item";
import EditContactModal from "../../components/modals/edit-contact-modal";
import PageTitle from "../../components/screens/page-title";
import { getJobTypes, getCompanies, getStates, getLocalities, createRecord } from "../../services/apiService";
import { showSuccessToast, showErrorToast, notifications } from "../../utils/notifications";
import { useTwilioCall } from "../../hooks/useTwilioCall";
import { getTodayISO } from "../../utils/dateUtils";
import usePageTitle from "../../hooks/usePageTitle";

const CreateRecord = () => {
  usePageTitle('Crear Expediente');
  const navigate = useNavigate();
  const { call: makeCall } = useTwilioCall();
  const [formData, setFormData] = useState({
    company: null,
    date: getTodayISO(), // Fecha de hoy preseleccionada
    status: null,
    jobType: null,
    companyNumber: "",
    clientName: "",
    clientAddress: "",
    clientLocality: null,
    clientNIF: "",
    billingName: "",
    billingAddress: "",
    billingLocality: null,
    billingNIF: "",
    service: "",
    contactName: "",
    contactAddress: "",
    contactPhone: "",
    contactInfo: "",
    contactRol: null,
  });
  const [dropdownKey, setDropdownKey] = useState(0);
  const [isBillingDifferent, setIsBillingDifferent] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [editingContact, setEditingContact] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [jobTypeOptions, setJobTypeOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [localityOptions, setLocalityOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Función para obtener el usuarioId del localStorage
  const getUsuarioId = () => {
    return localStorage.getItem("usuarioId");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDropdownSelect = (field, option) => {
    setFormData({ ...formData, [field]: option });
  };

  const handleBillingCheckboxChange = () => {
    setIsBillingDifferent(!isBillingDifferent);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación básica
    if (!formData.company) {
      showErrorToast("Por favor selecciona una compañía");
      return;
    }
    if (!formData.date) {
      showErrorToast("Por favor ingresa una fecha");
      return;
    }
    if (!formData.clientName) {
      showErrorToast("Por favor ingresa el nombre del cliente");
      return;
    }
    if (!formData.clientAddress) {
      showErrorToast("Por favor ingresa el domicilio del cliente");
      return;
    }
    if (!formData.clientLocality) {
      showErrorToast("Por favor selecciona la localidad del cliente");
      return;
    }
    
    setIsSubmitting(true);

    try {
      console.log("Datos del formulario:", formData);
      console.log("Estado seleccionado:", formData.status);
      console.log("Contactos:", contacts);

      // Preparar los datos para enviar a la API
      const recordData = {
        usuarioId: getUsuarioId(), // Obtener el usuarioId del localStorage
        companiaId: formData.company?.value || null,
        fecha: formData.date || null,
        estado: formData.status?.codigo || formData.status?.value || "PTE_ASIGNAR_VISITA", // Usar código de estado
        tipoTrabajoId: formData.jobType?.value || null,
        numeroCompania: formData.companyNumber || "",
        servicio: formData.service || "",
        nombreCliente: formData.clientName || "",
        domicilioCliente: formData.clientAddress || "",
        localidadClienteId: formData.clientLocality?.value || null,
        nifCliente: formData.clientNIF || "",
        difFacturacion: isBillingDifferent,
        nombreFacturacion: isBillingDifferent ? formData.billingName || "" : "",
        domicilioFacturacion: isBillingDifferent ? formData.billingAddress || "" : "",
        localidadFacturacionId: isBillingDifferent ? (formData.billingLocality?.value || null) : null,
        nifFacturacion: isBillingDifferent ? formData.billingNIF || "" : "",
        contactos: contacts.map(contact => ({
          nombre: contact.name || "",
          telefono: contact.phone || "",
          piso: contact.address || "",
          info: contact.info || "",
          contactoRol: contact.rol?.value || "AFECTADO"
        }))
      };

      console.log("Enviando datos:", recordData);

      // Enviar datos a la API
      const response = await createRecord(recordData);
      
      console.log("Expediente creado exitosamente:", response);
      showSuccessToast(notifications.create.success("Expediente"));
      
      // Navegar al expediente recién creado
      // Buscar el ID del expediente en diferentes posibles campos de la respuesta
      const expedienteId = response?.expedienteId || response?.id || response?.numeroExpediente;
      
      if (expedienteId) {
        console.log("Navegando al expediente:", expedienteId);
        navigate(`/record/${expedienteId}`);
      } else {
        console.warn("No se recibió ID de expediente en la respuesta:", response);
        // Si no hay ID de expediente, navegar a la lista de expedientes
        navigate("/records");
      }

    } catch (error) {
      console.error("Error al crear expediente:", error);
      showErrorToast(error.response?.data?.message || notifications.create.error("el expediente"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const addContact = (e) => {
    e.preventDefault();

    // Validar que el nombre sea obligatorio
    if (!formData.contactName || !formData.contactName.trim()) {
      showErrorToast("El nombre del contacto es obligatorio");
      return;
    }

    const newContact = {
      id: Date.now(),
      name: formData.contactName,
      address: formData.contactAddress,
      phone: formData.contactPhone,
      info: formData.contactInfo,
      rol: formData.contactRol,
    };

    setContacts([...contacts, newContact]);

    // Limpiar los campos de contacto después de agregarlo
    setFormData({
      ...formData,
      contactName: "",
      contactAddress: "",
      contactPhone: "",
      contactInfo: "",
      contactRol: null, // Asegurarse de que se resetea a null
    });
    setDropdownKey((prev) => prev + 1);
  };
  // Función para eliminar un contacto
  const removeContact = (contactId) => {
    setContacts(contacts.filter((contact) => contact.id !== contactId));
  };

  const handleContactClick = (contact) => {
    console.log("Contact clicked:", contact);
    console.log("Contact rol:", contact.rol);
    console.log("Available rolOptions:", rolOptions);
    
    // Encontrar el objeto rol correcto en rolOptions
    let selectedRol = null;
    if (contact.rol) {
      // Si contact.rol es un objeto con value, buscar por value
      if (typeof contact.rol === 'object' && contact.rol.value) {
        selectedRol = rolOptions.find(option => option.value === contact.rol.value);
        console.log("Found rol by object value:", selectedRol);
      } 
      // Si contact.rol es un string, buscar por value
      else if (typeof contact.rol === 'string') {
        selectedRol = rolOptions.find(option => option.value === contact.rol);
        console.log("Found rol by string value:", selectedRol);
      }
      // Si no encuentra nada, usar el objeto original
      else {
        selectedRol = contact.rol;
        console.log("Using original rol:", selectedRol);
      }
    }

    console.log("Final selectedRol:", selectedRol);

    setFormData({
      ...formData,
      contactName: contact.name,
      contactAddress: contact.address,
      contactPhone: contact.phone,
      contactInfo: contact.info || "",
      contactRol: selectedRol,
    });
    // Forzar re-render del dropdown para que muestre el valor seleccionado
    setDropdownKey((prev) => prev + 1);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setIsEditModalOpen(true);
  };

  const handleCallContact = (contact) => {
    if (contact.phone) {
      makeCall(contact.phone);
      showSuccessToast(`Llamando a ${contact.name}...`);
    } else {
      showErrorToast("El contacto no tiene número de teléfono");
    }
  };

  const handleSaveContact = (updatedContact) => {
    // Actualizar el contacto en la lista local
    const updatedContacts = contacts.map(contact => 
      contact.id === updatedContact.id ? updatedContact : contact
    );
    setContacts(updatedContacts);
    setIsEditModalOpen(false);
    setEditingContact(null);
    showSuccessToast("Contacto actualizado");
  };

  // Verificar si los campos obligatorios del contacto están completos
  const isContactFormValid = () => {
    return formData.contactName && formData.contactName.trim() !== "";
  };

   /*const companyOptions = [
    { value: "company1", label: "Compañía 1" },
    { value: "company2", label: "Compañía 2" },
  ];

  const statusOptions = [
    { value: "active", label: "Activo" },
    { value: "inactive", label: "Inactivo" },
  ];

 const jobTypeOptions = [
    { value: "fulltime", label: "Tiempo Completo" },
    { value: "parttime", label: "Medio Tiempo" },
  ];*/

  const rolOptions = [
    { value: "PRESIDENTE", label: "Presidente" },
    { value: "AFECTADO", label: "Afectado" },
    { value: "CONSERJE", label: "Conserje" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch job types
        const jobTypes = await getJobTypes();
        const jobTypeOptions = jobTypes.map((item) => ({ value: item.id, label: item.nombre }));
        setJobTypeOptions(jobTypeOptions);

        // Fetch companies
        const companies = await getCompanies();
        setCompanyOptions(companies.map((item) => ({ value: item.id, label: item.nombre })));

        // Fetch states
        const states = await getStates();
        console.log("Estados recibidos de la API:", states);
        const statusOptions = states.map((item) => ({ 
          value: item.id, 
          label: item.nombre,
          codigo: item.codigo // Incluir el código del estado
        }));
        setStatusOptions(statusOptions);

        // Fetch localities
        const localities = await getLocalities();
        setLocalityOptions(localities.map((item) => ({ value: item.id, label: `${item.municipio} (${item.codigoPostal})` })));

        // Preseleccionar el primer estado y tipo de trabajo
        setFormData(prevFormData => ({
          ...prevFormData,
          status: statusOptions.length > 0 ? statusOptions[0] : null,
          jobType: jobTypeOptions.length > 0 ? jobTypeOptions[0] : null,
        }));

      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <PageTitle iconName="fileFilled" title="Crear expediente" />
      
      <form onSubmit={handleSubmit}>
        <FormSection title="General" style={{ marginBottom: "24px" }}>
          <div className={styles.sectionRow}>
            <Dropdown
              label={"Compañía"}
              options={companyOptions}
              onSelect={(option) => handleDropdownSelect("company", option)}
              style={{ marginTop: "3px", width: "100%" }}
              mainStyle={{ flex: 1 }}
            />
            <Input
              label="Fecha"
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Dropdown
              label={"Estado"}
              options={statusOptions}
              onSelect={(option) => handleDropdownSelect("status", option)}
              style={{ marginTop: "3px", width: "100%" }}
              mainStyle={{ flex: 1 }}
              selected={formData.status}
            />
            <Dropdown
              label={"Tipo de Trabajo"}
              options={jobTypeOptions}
              onSelect={(option) => handleDropdownSelect("jobType", option)}
              style={{ marginTop: "3px", width: "100%" }}
              selected={formData.jobType}
              mainStyle={{ flex: 1 }}
            />
            <Input
              label="Número de Compañía"
              name="companyNumber"
              placeholder={"Numero de Compañía"}
              value={formData.companyNumber}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
          </div>
        </FormSection>

        <FormSection style={{ marginBottom: "24px" }} title="Servicios">
          <textarea
            name="service"
            placeholder={"Servicios necesarios"}
            value={formData.service}
            onChange={handleInputChange}
            className={styles.workTextarea}
            style={{
              width: "100%",
              height: "96px",
              paddingTop: "12px",
              paddingBottom: "12px",
              paddingLeft: "12px",
              paddingRight: "12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontFamily: "var(--font-roboto)",
              fontSize: "14px",
              color: "black",
              resize: "none",
              outline: "none",
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              overflow: "auto",
            }}
            spellCheck="true"
          />
        </FormSection>

        <FormSection
          title="Cliente"
          style={{ marginBottom: "24px" }}
          renderRight={() => (
            <Checkbox
              label="Facturación diferente"
              checked={isBillingDifferent}
              onChange={handleBillingCheckboxChange}
            />
          )}
        >
          <div className={styles.sectionRow}>
            <Input
              label="Nombre"
              name="clientName"
              placeholder={"Nombre del cliente"}
              value={formData.clientName}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Input
              label="Domicilio"
              name="clientAddress"
              placeholder={"Domicilio del cliente"}
              value={formData.clientAddress}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Dropdown
              label={"Localidad"}
              options={localityOptions}
              onSelect={(option) => handleDropdownSelect("clientLocality", option)}
              style={{ marginTop: "3px", width: "100%" }}
              mainStyle={{ flex: 1 }}
              selected={formData.clientLocality}
            />
            <Input
              label="NIF"
              name="clientNIF"
              placeholder={"NIF del cliente"}
              value={formData.clientNIF}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
          </div>
        </FormSection>

        {isBillingDifferent && (
          <FormSection
            title="Facturación diferente"
            style={{ marginBottom: "24px" }}
          >
            <div className={styles.sectionRow}>
              <Input
                label="Nombre"
                name="billingName"
                placeholder={"Nombre de facturación"}
                value={formData.billingName}
                onChange={handleInputChange}
                style={{ flex: 1 }}
              />
              <Input
                label="Domicilio"
                name="billingAddress"
                placeholder={"Domicilio de facturación"}
                value={formData.billingAddress}
                onChange={handleInputChange}
                style={{ flex: 1 }}
              />
              <Dropdown
                label={"Localidad"}
                options={localityOptions}
                onSelect={(option) => handleDropdownSelect("billingLocality", option)}
                style={{ marginTop: "3px", width: "100%" }}
                mainStyle={{ flex: 1 }}
                selected={formData.billingLocality}
              />
              <Input
                label="NIF"
                name="billingNIF"
                placeholder={"NIF de facturación"}
                value={formData.billingNIF}
                onChange={handleInputChange}
                style={{ flex: 1 }}
              />
            </div>
          </FormSection>
        )}

        <FormSection title="Contactos" style={{ marginBottom: "24px" }}>
          <div className={styles.sectionRow}>
            <Input
              label="Nombre"
              name="contactName"
              placeholder={"Nombre del contacto"}
              value={formData.contactName}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Input
              label="Domicilio (Piso, Puerta, etc.)"
              name="contactAddress"
              placeholder={"Domicilio del contacto"}
              value={formData.contactAddress}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Input
              label="Telefono"
              name="contactPhone"
              placeholder={"Telefono del contacto"}
              value={formData.contactPhone}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Input
              label="Información adicional"
              name="contactInfo"
              placeholder={"Información adicional"}
              value={formData.contactInfo}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Dropdown
              key={dropdownKey}
              label={"Rol"}
              options={rolOptions}
              onSelect={(option) => handleDropdownSelect("contactRol", option)}
              style={{ marginTop: "3px", width: "100%" }}
              mainStyle={{ flex: 1 }}
              selected={formData.contactRol}
            />
          </div>
          {/* Lista de contactos */}
          {contacts.length > 0 && (
            <div className={styles.contactsList}>
              {contacts.map((contact) => (
                <ContactItem
                  key={contact.id}
                  contact={contact}
                  onRemove={removeContact}
                  onClick={handleContactClick}
                  onEdit={handleEditContact}
                  onCall={handleCallContact}
                />
              ))}
            </div>
          )}
          <Button
            type="button"
            onClick={addContact}
            disabled={!isContactFormValid()}
            style={{
              opacity: isContactFormValid() ? 1 : 0.5,
              cursor: isContactFormValid() ? "pointer" : "not-allowed",
              marginTop: "24px",
              width: "fit-content",
            }}
          >
            AÑADIR CONTACTO
          </Button>
        </FormSection>
        <Button 
          type="submit" 
          disabled={isSubmitting}
          style={{
            opacity: isSubmitting ? 0.6 : 1,
            cursor: isSubmitting ? "not-allowed" : "pointer"
          }}
        >
          {isSubmitting ? "CREANDO EXPEDIENTE..." : "CREAR EXPEDIENTE"}
        </Button>
      </form>

      {/* Modal para editar contacto */}
      <EditContactModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingContact(null);
        }}
        contact={editingContact}
        onSave={handleSaveContact}
        rolOptions={rolOptions}
      />
    </div>
  );
};

export default CreateRecord;
